import { Typography, VerticalSpace } from '@chargeafter/ui';
import { AppView, DEVICE_SELECTION_MODAL_TEXTS } from '../constants';
import { useConfirm } from '../services/confirmationModalManager';
import { ModalHeaderWithExitButton } from './ModalHeaderWithExitButton';
import {
  ConfigurationNew,
  useGetConfiguration,
} from '../services/configManagerNew';
import { Box, styled } from '@mui/material';
import { confirmModalStyles } from './confirmModalStyles';

const StyledContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  color: theme.palette.grey[800],
  minWidth: theme.spacing(100),
}));

const parseDeviceLendersNames = (
  configuration: ConfigurationNew | null,
): {
  consumerDeviceOnlyLendersNames?: string[];
  merchantDeviceAllowedLenderNames?: string[];
} => {
  return (
    configuration?.lenders?.reduce(
      (acc, lender) => {
        const {
          consumerDeviceOnlyLendersNames,
          merchantDeviceAllowedLenderNames,
        } = acc;

        if (
          !lender?.settings?.checkout?.options.allowMerchantDevice &&
          lender?.settings?.checkout?.options.allowConsumerDevice
        ) {
          consumerDeviceOnlyLendersNames.push(lender.name);
        }

        if (lender?.settings?.checkout?.options.allowMerchantDevice) {
          merchantDeviceAllowedLenderNames.push(lender.name);
        }

        return acc;
      },
      {
        consumerDeviceOnlyLendersNames: [],
        merchantDeviceAllowedLenderNames: [],
      } as {
        consumerDeviceOnlyLendersNames: string[];
        merchantDeviceAllowedLenderNames: string[];
      },
    ) || {}
  );
};

const formatLenderNames = (lendersNames?: string[]) => {
  if (!lendersNames) {
    return '';
  }

  return lendersNames.length > 1
    ? `${lendersNames.slice(0, -1).join(', ')} and ${
        lendersNames[lendersNames.length - 1]
      }`
    : lendersNames[0];
};

export const useDeviceSelectionModal = () => {
  const deviceSelection = useConfirm(AppView.DeviceSelectionModal);
  const configuration = useGetConfiguration();

  const { consumerDeviceOnlyLendersNames, merchantDeviceAllowedLenderNames } =
    parseDeviceLendersNames(configuration);

  const formattedNotAllowedLendersNames = formatLenderNames(
    consumerDeviceOnlyLendersNames,
  );
  const formattedAllowedLendersNames = formatLenderNames(
    merchantDeviceAllowedLenderNames,
  );

  return async () => {
    try {
      await deviceSelection({
        ...confirmModalStyles,
        allowClose: true,
        dialogProps: {
          PaperProps: {
            'data-testid': 'device-selection-modal',
          },
        },
        title: (
          <ModalHeaderWithExitButton
            title={DEVICE_SELECTION_MODAL_TEXTS.TITLE}
          />
        ),
        confirmationText: DEVICE_SELECTION_MODAL_TEXTS.CONFIRMATION_BUTTON,
        cancellationText: DEVICE_SELECTION_MODAL_TEXTS.CANCELLATION_BUTTON,
        content: (
          <StyledContainer>
            <Typography variant="body3U" fontWeight="bold">
              {DEVICE_SELECTION_MODAL_TEXTS.CONTENT_CTA}
            </Typography>
            <VerticalSpace units={1} />
            <Typography variant="body1">
              {formattedNotAllowedLendersNames &&
                `${DEVICE_SELECTION_MODAL_TEXTS.CONTENT_DESCRIPTION1} ${formattedNotAllowedLendersNames} ${DEVICE_SELECTION_MODAL_TEXTS.CONTENT_DESCRIPTION2}`}
              <br />
              {formattedAllowedLendersNames &&
                `${DEVICE_SELECTION_MODAL_TEXTS.CONTENT_DESCRIPTION3} ${formattedAllowedLendersNames}.`}
            </Typography>
          </StyledContainer>
        ),
      });
      return true;
    } catch (e) {
      return false;
    }
  };
};
